import { Helmet } from "react-helmet";
import '../pages/locations/MukhaFall/MukhaFall.css';
import { BookSection } from "../components/BookSection";

export const SonbhadraPage = () => {
    return (
        <div className="mukha-fall-container">
               
               <Helmet>
                <title>About Sonbhadra District | History, Geography, Tourism & Economy</title>
                <meta name="description" content="Learn about Sonbhadra (Sonebhadra) district, its history, geography, climate, industries, and cultural significance. Discover why it's known as the 'Energy Capital of India' or 'Switzerland of India' or 'Mini Goa' or 'Gupt Kashi'." />
                <meta name="keywords" content="Sonbhadra, Sonebhadra, सोनभद्र, सोनभद्र उत्तर प्रदेश, son, about sonbhadra, renukoot, about Sonbhadra district, Sonbhadra tourism, Sonbhadra history, sonebhadra history, Hindalco industry, hindalco, Sonbhadra industries, Sonebhadra industries, Energy Capital of India, Sonbhadra geography, Switzerland of India, Mini Goa or Gupt Kashi" />
                <meta name="author" content="Sonebhadra Tourism Pvt. Ltd." />
                <link rel="canonical" href="https://sonbhadratourism.com/sonbhadra" />
               </Helmet>   

            <div className="fancy-heading">
                <h1>About Sonbhadra</h1>
                <p className="sub-heading">
                    Sonbhadra’s strategic location, rich history, and abundant natural resources have shaped its transformation into a significant industrial, energy hub, and tourism zone. The district plays a crucial role in India's power generation and industrial output, making it an essential region in Uttar Pradesh's economic landscape.
                </p>
            </div>

            <div className="image-item">
                <img
                    src={require('../media/tourism/Slider image/Best Religious tour (7).png')}
                    alt="Mukha Fall 3"
                    className="gallery-image"
                />
            </div>

            <div className="description">
                <h2>History of Sonbhadra</h2>

                <h3>Overview</h3>
                <p>
                    Sonbhadra (Hindi: सोनभद्र) is the second-largest district in Uttar Pradesh, India. It is the only district in the country that shares its borders with four states: Madhya Pradesh, Chhattisgarh, Jharkhand, and Bihar. This unique fact was once featured as a question worth 50 lakh rupees on the popular TV show <em>Kaun Banega Crorepati</em>.
                </p>
                <p>
                    The district spans an area of 6,788 km² and, as per the 2011 census, has a population of 1,862,559, with a density of 270 people per km². The district headquarters is located in Robertsganj.
                </p>
                <p>
                    Sonbhadra is an industrial hub rich in natural resources such as bauxite, limestone, coal, and gold, earning it the title "Energy Capital of India" due to its numerous power plants.
                </p>

                <h3>Historical Significance</h3>
                <p>Sonbhadra has a deep historical and cultural heritage, with references found in <em>Ramayana</em> and <em>Mahabharata</em>. It is believed that the area was a site where many rulers, including those captured by Jarasandh, were imprisoned during the Mahabharata era.</p>
                <ul>
                    <li>Up to the 5th century, the region was home to communities like Bhars, Cheros, Searis, Kols, and Kherwars.</li>
                    <li>During the 9th century BCE, the Brahmadutt dynasty split due to the influence of the Nagas.</li>
                    <li>The region was once part of Kaushala and Magadha in the 8th and 7th centuries BCE.</li>
                    <li>The Kushans and Nagas held dominance before the Gupta period.</li>
                    <li>After Harshvardhan's death in the 7th century, the region fell under the rule of the Gurjars and Pratihars until Mahmud of Ghazni's invasion in 1025.</li>
                    <li>During the 18th century, it came under the rule of the Narayan dynasty of Benares before British control in the late 1770s.</li>
                    <li>In 1989, Sonbhadra was officially carved out of Mirzapur district.</li>
                </ul>

                <h3>Forts in Sonbhadra</h3>
                <ul>
                    <li><strong>Agori Fort</strong> – Previously ruled by Madan Shah.</li>
                    <li><strong>Vijaygarh Fort</strong> – Once controlled by Raja Chait Singh of Benares.</li>
                    <li><strong>Sodhrigarh Fort</strong> – Governed by Garhwal kings.</li>
                </ul>
                <p>The heroine of the famous novel <em>Chandrakanta</em> by Devaki Nandan Khatri was a princess of Vijaygarh and the daughter of King Jay Singh.</p>

                <h3>Geography</h3>
                <ul>
                    <li>The northern third consists of a plateau, drained by tributaries of the Ganges like the Belan and Karmanasha rivers.</li>
                    <li>The Son River flows west to east through the district, forming a valley south of the Kaimur Range.</li>
                    <li>The southern region is hilly, interspersed with fertile valleys.</li>
                    <li>The Rihand River, originating in Surguja, Chhattisgarh, joins the Son in the district's center.</li>
                    <li>The Govind Ballabh Pant Sagar reservoir on the Rihand River lies partly in Sonbhadra and partly in Madhya Pradesh.</li>
                </ul>
                <p>Robertsganj serves as the district headquarters.</p>

                <h3>Climate</h3>
                <ul>
                    <li><strong>Summer:</strong> 30°C–46°C</li>
                    <li><strong>Winter:</strong> 2°C–15°C</li>
                    <li><strong>Monsoon:</strong> Pleasant weather from July to October</li>
                </ul>

                <h3>Ecology</h3>
                <p>
                    The northern part of Sonbhadra falls within the Lower Gangetic plains moist deciduous forests, while the southern portion belongs to the Chhota Nagpur dry deciduous forests. The <strong>Kaimoor Wildlife Sanctuary</strong> extends across the Kaimur Range and reaches the Son River.
                </p>

                <h3>Economy & Industry</h3>
                <p>
                    Sonbhadra, known as the "Energy Capital of India," hosts multiple power plants, including:
                </p>
                <ul>
                    <li><strong>Singrauli Super Thermal Power Station (2000 MW)</strong> – India's first NTPC power plant.</li>
                    <li><strong>Vindhyachal Thermal Power Station (4760 MW)</strong> – Largest capacity in India.</li>
                    <li><strong>Rihand Thermal Power Station (3000 MW)</strong> – Located in Renukut.</li>
                    <li>Other notable power plants: Anpara (UPRVUNL), Obra (UPRVUNL), Renusagar (Hindalco), and Pipri-Hydro (UPRVUNL).</li>
                    <li>NCL (Northern Coalfields Limited) operates major coal mines here.</li>
                    <li>Hindalco runs a significant aluminum plant in Renukut.</li>
                </ul>
                <p>
                    Despite its industrial success, Sonbhadra was listed among the 250 most backward districts in India by the Ministry of Panchayati Raj in 2006 and continues to receive funding from the Backward Regions Grant Fund Programme (BRGF). For more details visit Sonbhadra Official government website. <a href="https://sonbhadra.nic.in/history/" target="_blank" rel="noopener noreferrer">Sonbhadra Official Government Site</a>
                </p>
            </div>

            <div className="image-item">
                <img
                    src={require('../media/tourism/Slider image/Best Religious tour (8).png')}
                    alt="Mukha Fall 3"
                    className="gallery-image"
                />
            </div>

            <BookSection />
        </div>
    );

    
};
